import {
  flow,
  getRoot,
  getSnapshot,
  Instance,
  types,
  cast,
  addDisposer,
  onPatch,
  isAlive,
} from "mobx-state-tree";
import { validateSceneRename } from "../helpers/sceneName";
import { scenePath } from "../utils/navigation";
import { withSceneFlowNode } from "../helpers/sceneFlowNode";
import { generateClient, post } from "aws-amplify/api";
import { insertIntoGraphQlString } from "modifygraphqlstring";
import { Slide, toast } from "react-toastify";
import {
  FLOW_DIAGRAM_NODE_TYPE,
  compress as playerCompress,
} from "@blings/blings-player";
import type { SettingsApiEnv } from "@blings/blings-player";
import { updateProjectVersion } from "../graphql/mutations";
import { customUpdateProjectVersionNodeIds } from "../graphql/customQueries";
import { Image2WebpMicroserviceResponse } from "../types/microservices";
import { canRunWebP, isImage } from "../utils/image";
import { fetchAuthSession } from "aws-amplify/auth";
import { uploadData } from "@aws-amplify/storage";
import { UploadFile } from "antd";
import { savedDate2Relative } from "../components/playground-top-bar/PublishStatus";
import {
  Mod,
  ProjectVersion,
  Experiment,
  ExperimentOptimizationTechnique,
  UpdateProjectVersionInput,
  UpdateProjectVersionMutationVariables,
} from "../API";
import { emptySchema } from "../helpers/jsonShema.helpers";
import {
  subscriptionManager,
  Subscription,
} from "../utils/subscriptionManager";
import {
  createVersion,
  getLatestProjectVersion,
  getPublishedProject,
  getPublishedProjectBasicInfo,
  persistNormalizedNodeIds,
  publishVersion,
} from "../utils/projectTools";
import {
  normalizeFlowNodeIds,
  rewriteNodeRefs,
} from "../helpers/normalizeFlowNodeIds";
import confirmationModal from "../components/BlingsModal/CustomConfirm";
import { RootInstance, rootStore } from "./main";
import { CDN, getENV, MICROSERVICE_API_URLS } from "./consts";
import {
  AsyncOpState,
  asyncOpStateType,
  updateAsyncStatus,
} from "./async-op-state";
import { ILineupModel } from "./FlowStore";
import { signOutAndDeleteCookies } from "@blings/shared-auth-component";
import { del } from "idb-keyval";
import { flowsStore } from "./FlowStore";
import { getAccessTokenForLambdaAuth } from "../utils/authHelpers";
import { toProjectFontInput } from "../helpers/fontOverrides";
import { getVideoPartFonts } from "../helpers/videoPartFonts";
import { mergeProjectVersionSubscriptionPatch } from "./projectVersionSubscriptionMerge";
import { encodeFlowDiagramSourceHandles } from "../utils/flowDiagramSourceHandle";
import {
  guardProjectMutation,
  setProjectConflictBaseline,
  updateProjectConflictBaselineFields,
} from "../utils/projectConflictGuard";
import type { ProjectConflictField } from "../utils/projectConflictCopy";
import { buildStarterSceneAnimation } from "../helpers/layerCreation";

import {
  applyProjectReferenceRenames,
  ProjectReferenceRename,
} from "./projectReferenceRenames";
import type { DynamicReferenceScope } from "../helpers/modKeyReferenceRename";

const client = generateClient();

const animationConflictFields: ProjectConflictField[] = [
  { path: "videoParts", domain: "connectors", label: "animation" },
];

const connectorConflictFields: ProjectConflictField[] = [
  { path: "videoParts", domain: "connectors", label: "connectors" },
];

const experimentConflictFields: ProjectConflictField[] = [
  { path: "experiments", domain: "experiments" },
];

const publishSceneConflictFields: ProjectConflictField[] = [
  ...animationConflictFields,
  ...connectorConflictFields,
];

const sceneListConflictFields: ProjectConflictField[] = [
  { path: "videoParts", domain: "connectors", label: "scenes" },
];

const sceneNameConflictFields: ProjectConflictField[] = [
  { path: "videoParts", domain: "connectors", label: "scene names" },
];

const sceneDescriptionConflictFields: ProjectConflictField[] = [
  { path: "videoParts", domain: "connectors", label: "scene description" },
];

const flowConflictFields: ProjectConflictField[] = [
  { path: "flowDiagram", domain: "flow" },
];

const perVideoDataFields: ProjectConflictField[] = [
  { path: "stateJsonSchemaStr", domain: "data", label: "per-video data" },
];

const liveControlFields: ProjectConflictField[] = [
  { path: "settings", domain: "data", label: "live control data" },
  { path: "settingsJsonSchemaStr", domain: "data", label: "live control data" },
];

const nonScenePublishFields: ProjectConflictField[] = [
  ...flowConflictFields,
  ...perVideoDataFields,
  ...liveControlFields,
  { path: "fontOverrides", domain: "branding" },
];

async function guardDraftSave(
  projectId: string,
  fields: ProjectConflictField[],
  next: unknown
) {
  return guardProjectMutation({
    projectId,
    target: "draft",
    operation: "save",
    fields,
    next,
    loadLatest: () => getLatestProjectVersion(projectId),
  });
}

async function guardPublishedSave(
  projectId: string,
  fields: ProjectConflictField[],
  next: unknown,
  operation: "save" | "publish" = "save"
) {
  return guardProjectMutation({
    projectId,
    target: "published",
    operation,
    fields,
    next,
    loadLatest: () => getPublishedProject(projectId),
  });
}

function getDirtySceneConflictFields(
  root: RootInstance
): ProjectConflictField[] {
  const fields: ProjectConflictField[] = [];
  if (root.playerStore.hasUnsavedJsonDraftChanges) {
    fields.push(...animationConflictFields);
  }
  if (
    root.modsStore.hasUnsavedChanges ||
    root.platformStore.hasUnsavedConnectorKeyRenames
  ) {
    fields.push(...connectorConflictFields);
  }
  if (root.experimentStore.hasUnsavedChanges) {
    fields.push(...experimentConflictFields);
  }
  return fields;
}

function getPublishConflictFields(root: RootInstance): ProjectConflictField[] {
  const dirtySceneFields = getDirtySceneConflictFields(root);
  return [
    ...(dirtySceneFields.length
      ? dirtySceneFields
      : [...publishSceneConflictFields, ...experimentConflictFields]),
    ...nonScenePublishFields,
  ];
}

const buildSceneJsonAssetName = (sceneName: string) => {
  const normalizedName =
    sceneName
      .trim()
      .replace(/[^\w.-]+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "") || "scene";

  return `${normalizedName}.json`;
};

const buildUniqueSceneName = (
  existingNames: Iterable<string>,
  baseName = "New scene"
) => {
  const names = new Set(existingNames);
  let candidate = baseName;
  let index = 2;

  while (names.has(candidate)) {
    candidate = `${baseName} ${index}`;
    index += 1;
  }

  return candidate;
};

const FlowDiagramEdgeModel = types.model("FlowDiagramEdge", {
  source: types.string,
  target: types.string,
  sourceHandle: types.maybeNull(types.string),
});
const FlowDiagramNodeModel = types.model("FlowDiagramNode", {
  id: types.string,
  label: types.string,
  type: types.enumeration(
    "FlowDiagramNodeType",
    Object.values(FLOW_DIAGRAM_NODE_TYPE)
  ),
  data: types.maybeNull(types.string),
  lineupId: types.maybeNull(types.string),
});
export const FlowDiagramModel = types
  .model("FlowDiagram", {
    edges: types.array(FlowDiagramEdgeModel),
    nodes: types.array(FlowDiagramNodeModel),
    isDirty: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get lineups() {
      return self.nodes.reduce<Array<string>>((acc, node) => {
        if (node.lineupId) {
          acc.push(node.lineupId);
        }
        return acc;
      }, []);
    },
    get asFlows(): Array<ILineupModel> {
      const flowsGroups = {};
      const tempEdges = self.edges.map((edge) => ({ ...edge }));

      self.nodes.forEach((node) => {
        if (node.type === "scene" && node.lineupId) {
          const firstScene = {
            id: node.id,
            label: node.label,
            data: node.data,
          };
          flowsGroups[node.lineupId] = {
            id: node.id,
            lineupId: node.lineupId,
            scenes: [firstScene],
            isDefault: false,
          };

          let count = 0;
          const egdesLength = tempEdges.length;
          let sourceNodeId = node.id;

          while (
            tempEdges.length > 0 &&
            count < egdesLength &&
            sourceNodeId !== ""
          ) {
            const currentSourceNodeId = sourceNodeId;
            const edge = tempEdges.findIndex(
              (edge) => edge.source === currentSourceNodeId
            );
            if (edge > -1) {
              const nextNode = self.nodes.find(
                (node) => node.id === tempEdges[edge].target
              );
              if (nextNode && nextNode.id !== currentSourceNodeId) {
                sourceNodeId = nextNode.id;
                flowsGroups[node.lineupId].scenes.push({
                  id: nextNode.id,
                  label: nextNode.label,
                  lineupId: nextNode.lineupId,
                  data: nextNode.data,
                });
              } else {
                sourceNodeId = "";
              }
              tempEdges.splice(edge, 1);
            }
            count++;
          }
        }
      });
      return Object.values(flowsGroups);
    },
    get getFlows() {
      return self.nodes.reduce<Array<object>>((acc, node) => {
        if (node.lineupId && node.type === "lineup") {
          acc.push(node);
        }
        return acc;
      }, []);
    },
    get defaultLineup() {
      return this.lineups[0];
    },
    get hasUnsavedChanges() {
      return self.isDirty;
    },
    get flowDiagramDBFormat() {
      if (!self.nodes || !self.edges) return null;
      return encodeFlowDiagramSourceHandles({
        nodes: JSON.parse(JSON.stringify(self.nodes)),
        edges: JSON.parse(JSON.stringify(self.edges)),
      });
    },
  }))
  .actions((self) => ({
    isSceneInFlow(sceneId: string) {
      if (!self.nodes) return false;
      return self.nodes.some(
        (node) => node.type === "scene" && node.label === sceneId
      );
    },
    setNodesAndEdges(nodes: any[], edges: any[]) {
      self.nodes = cast(nodes);
      // MST stores sourceHandle as string/null; GraphQL AWSJSON encoding happens at save boundaries.
      const sanitizedEdges = edges.map((e) => ({
        ...e,
        sourceHandle: e.sourceHandle != null ? String(e.sourceHandle) : null,
      }));
      self.edges = cast(sanitizedEdges);
    },
    setIsDirty(value: boolean) {
      self.isDirty = value;
    },
    updateSceneNameReferences(oldName: string, newName: string) {
      if (!self.nodes) return false;

      let hasUpdates = false;
      self.nodes.forEach((node) => {
        if (node.type === "scene" && node.label === oldName) {
          node.label = newName;
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        self.isDirty = true;
      }

      return hasUpdates;
    },
    updateLineupIdReferences(oldLineupId: string, newLineupId: string) {
      if (!self.nodes) {
        return false;
      }

      let hasUpdates = false;
      self.nodes.forEach((node) => {
        if (node.lineupId === oldLineupId) {
          node.lineupId = newLineupId;
          hasUpdates = true;
        }
      });

      if (hasUpdates) {
        self.isDirty = true;
      }

      return hasUpdates;
    },
  }));
export type ProjectBasicInfo = {
  title: string;
  id: string;
  updatedAt: string;
  createdAt: string;
  playerVersionToUse: string;
  publishedAt?: string;
  thumbS3Url: string;
  experimentOptimizationTechnique: ExperimentOptimizationTechnique;
  projectAccountId: string;
};

export const PlatformModel = types
  .model({
    pendingProjectReferenceRenames: types.optional(
      types.array(types.frozen<ProjectReferenceRename & { id: number }>()),
      []
    ),
    initialProjectLoadSuccess: types.optional(types.boolean, false),
    selectedProjectId: types.maybeNull(types.string),
    selectedSceneName: types.maybeNull(types.string),
    selectedProjectBasicInfo: types.maybeNull(types.frozen<ProjectBasicInfo>()),
    selectedVideoPartName: types.maybeNull(types.string),
    allProjects: types.optional(
      types.array(types.frozen<ProjectBasicInfo>()),
      []
    ),
    projectWorkspaceVersion: types.maybeNull(types.frozen<ProjectVersion>()),
    projectWorkspaceFlowDiagram: types.maybeNull(FlowDiagramModel),
    uploadJsonAndImagesStatus: asyncOpStateType,
    saveModsStatus: asyncOpStateType,
    saveFlowStatus: asyncOpStateType,
    saveProjectSchemaStatus: asyncOpStateType,
    saveProjectLiveControlSchemaStatus: asyncOpStateType,
    uploadAssetStatus: asyncOpStateType,
    dynamicFormUploadAssetStatus: types.map(
      types.model({ id: types.identifier, status: asyncOpStateType })
    ),
    isLoadingProjectList: types.optional(types.boolean, false),
    isLoadingSelectedProject: types.optional(types.boolean, false),
    hasLoadedProjectList: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get hasUnsavedConnectorKeyRenames() {
      return self.pendingProjectReferenceRenames.length > 0;
    },
    get selectedVideoPart() {
      return self.projectWorkspaceVersion?.videoParts?.find(
        (vp) => vp.name === self.selectedVideoPartName
      );
    },
    get lineups() {
      return (
        self.projectWorkspaceVersion?.flowDiagram?.nodes?.reduce<Array<string>>(
          (acc, node) => {
            if (node.lineupId) {
              acc.push(node.lineupId);
            }
            return acc;
          },
          []
        ) || []
      );
    },
    get nodeIds() {
      return (
        self.projectWorkspaceVersion?.flowDiagram?.nodes?.map(
          (node) => node.id
        ) || []
      );
    },
    get platformStorageProjectAssetsRoot() {
      return `projects/${self.selectedProjectId}/assets`;
    },
    get selectedProjectDataSchema() {
      if (self.projectWorkspaceVersion?.stateJsonSchemaStr)
        return JSON.parse(self.projectWorkspaceVersion?.stateJsonSchemaStr);
      return emptySchema;
    },
    get selectedProjectLiveControlSchema() {
      if (self.projectWorkspaceVersion?.settingsJsonSchemaStr)
        return JSON.parse(self.projectWorkspaceVersion?.settingsJsonSchemaStr);
      return emptySchema;
    },
    get selectedProjectLiveControl() {
      if (self.projectWorkspaceVersion?.settings) {
        try {
          if (typeof self.projectWorkspaceVersion.settings === "object") {
            return self.projectWorkspaceVersion.settings;
          }
          return JSON.parse(self.projectWorkspaceVersion.settings);
        } catch (e) {
          return {};
        }
      }
      return {};
    },

    get selectedProjectHasLiveVersion(): boolean {
      try {
        const live =
          self.selectedProjectBasicInfo?.updatedAt &&
          (self.projectWorkspaceVersion?.updatedAt ||
            self.selectedProjectBasicInfo?.updatedAt ===
              self.selectedProjectBasicInfo?.createdAt);
        return !!live;
      } catch (e) {
        return false;
      }
    },
    get selectedProjectIsLatestVersionPublished(): boolean {
      const published =
        self.selectedProjectBasicInfo?.publishedAt ||
        self.selectedProjectBasicInfo?.updatedAt;
      const workspace = self.projectWorkspaceVersion?.updatedAt;
      if (!published || !workspace) return false;
      return !!published && published > workspace;
    },
    get publishedTime() {
      if (this.selectedProjectHasLiveVersion) {
        const editText = savedDate2Relative(
          self.selectedProjectBasicInfo?.updatedAt || ""
        );
        return `Published version.
        ${editText}`;
      }
      // else {
      //   if (projectWorkspaceVersion?.updatedAt) {
      //     const editText = savedDate2Relative(projectWorkspaceVersion.updatedAt);
      //     const savedDate = new Date(projectWorkspaceVersion?.updatedAt);
      //     return `Last edit was ${editText}`}
      //   }
      return null;
    },
    get videoScenes() {
      return self.projectWorkspaceVersion?.videoParts || [];
    },
    get selectedScene() {
      return self.selectedSceneName;
    },
  }))
  .views((self) => ({
    get hasUnsavedSceneChanges() {
      const {
        modsStore: { hasUnsavedChanges: hasUnsavedChangesConnectors },
        playerStore: { hasUnsavedJsonDraftChanges },
      } = getRoot<any>(self);

      return (
        hasUnsavedChangesConnectors ||
        hasUnsavedJsonDraftChanges ||
        self.hasUnsavedConnectorKeyRenames
      );
    },
    get inputNames() {
      const inputNames: string[] = [];
      if (
        self.projectWorkspaceVersion &&
        self.projectWorkspaceVersion.videoParts
      ) {
        const { modsStore } = getRoot<RootInstance>(self);
        const currentVideoPartName = self.selectedVideoPartName;
        const allOtherVideoParts =
          self.projectWorkspaceVersion.videoParts.filter(
            (vp) => vp.name !== currentVideoPartName
          );
        allOtherVideoParts
          .map((vp) => vp.modsArr)
          .forEach((ma) => {
            ma?.map((mod) => (mod?.dataStr ? JSON.parse(mod.dataStr) : []))
              .filter((mod) => mod.type === "interactiveInput")
              .forEach((mod) => {
                inputNames.push(mod.value);
              });
          });
        modsStore.mods.forEach((mod) => {
          mod.moddata.type === "interactiveInput" &&
            inputNames.push(mod.moddata.value);
        });
      }
      return inputNames;
    },
    /**
     * Verify if there are unsaved changes between the current state of the project and the last saved version
     */
    get hasUnsavedChanges() {
      //Check unsaved changes for connectors
      const {
        dynamicDataStore: {
          hasUnsavedChanges: hasUnsavedChangesSchema,
          hasUnsavedChangesPerVideo,
        },
        modsStore: { hasUnsavedChanges: hasUnsavedChangesConnectors },
        experimentStore: { hasUnsavedChanges: hasUnsavedChangesExperiments },
        playerStore: { hasUnsavedJsonDraftChanges },
      } = getRoot<any>(self);

      return (
        self.hasUnsavedConnectorKeyRenames ||
        hasUnsavedChangesConnectors ||
        hasUnsavedJsonDraftChanges ||
        hasUnsavedChangesSchema ||
        hasUnsavedChangesExperiments ||
        hasUnsavedChangesPerVideo ||
        self.projectWorkspaceFlowDiagram?.isDirty
      );
    },

    get isControlDeprecated() {
      const keys = Object.keys(self.selectedProjectLiveControl || {});
      return keys.length === 0;
    },
  }))
  .actions((self) => ({
    // ...updateAsyncStatus(self, 'uploadJsonAndImagesStatus'),
    updateUploadJsonAndImagesStatus: updateAsyncStatus(
      self,
      "uploadJsonAndImagesStatus"
    ),
    updateUploadAssetStatus: updateAsyncStatus(self, "uploadAssetStatus"),
    updateSaveModsStatus: updateAsyncStatus(self, "saveModsStatus"),
    updateSaveFlowStatus: updateAsyncStatus(self, "saveFlowStatus"),
    updateSaveProjectSchemaStatus: updateAsyncStatus(
      self,
      "saveProjectSchemaStatus"
    ),
    updateSaveProjectLiveControlSchemaStatus: updateAsyncStatus(
      self,
      "saveProjectLiveControlSchemaStatus"
    ),
    getDynamicFormAssetsUploadStatus(id: string) {
      return self.dynamicFormUploadAssetStatus.get(id)?.status;
    },
    setLoadingProjectList(isLoading: boolean) {
      self.isLoadingProjectList = isLoading;
    },
    setLoadingSelectedProject(isLoading: boolean) {
      self.isLoadingSelectedProject = isLoading;
    },

    setInitialProjectLoadSuccess(success: boolean) {
      self.initialProjectLoadSuccess = success;
    },
    setProjectWorkspaceFlowDiagramModel(flowDiagram: IFlowDiagramModel) {
      self.projectWorkspaceFlowDiagram = flowDiagram;
    },
    setSelectedSceneName(sceneName: string | null) {
      self.selectedSceneName = sceneName;
    },
    syncCreatedStarterScene(
      videoParts: NonNullable<ProjectVersion["videoParts"]>,
      sceneName: string
    ) {
      if (!self.projectWorkspaceVersion) {
        return;
      }

      self.projectWorkspaceVersion = {
        ...self.projectWorkspaceVersion,
        videoParts,
      };
      self.selectedSceneName = sceneName;
      self.selectedVideoPartName = sceneName;
    },
    updateLineupIdInProjectVersion(oldLineupId: string, newLineupId: string) {
      if (self.projectWorkspaceVersion?.flowDiagram?.nodes) {
        const updatedVersion = {
          ...self.projectWorkspaceVersion,
          flowDiagram: {
            ...self.projectWorkspaceVersion.flowDiagram,
            nodes: self.projectWorkspaceVersion.flowDiagram.nodes.map((node) =>
              node.lineupId === oldLineupId
                ? { ...node, lineupId: newLineupId }
                : node
            ),
          },
        };

        self.projectWorkspaceVersion = updatedVersion;
        if (self.projectWorkspaceFlowDiagram) {
          self.projectWorkspaceFlowDiagram.updateLineupIdReferences(
            oldLineupId,
            newLineupId
          );
        }

        return true;
      }
      return false;
    },
  }))
  .actions((self) => {
    let selectedProjectSubscription: Subscription | null = null;
    let projectVersionSubscription: Subscription | null = null;
    // One writer for connector/schema drafts, regardless of which UI starts it.
    let draftSaveTail: Promise<unknown> = Promise.resolve();
    let draftSaveGeneration = 0;
    let draftSaveOperation = 0;
    let nextRenameId = Math.max(
      0,
      ...self.pendingProjectReferenceRenames.map((rename) => rename.id || 0)
    );
    const staleDraftMessage =
      "Data changed while saving. Save again to include the latest changes.";
    function queueDraftSave<T>(
      save: (isCurrent: () => boolean) => Promise<T>
    ): Promise<T | false> {
      const generation = draftSaveGeneration;
      const result = draftSaveTail.then(() => {
        if (!isAlive(self) || generation !== draftSaveGeneration)
          return false as const;
        const operation = ++draftSaveOperation;
        return save(
          () =>
            isAlive(self) &&
            generation === draftSaveGeneration &&
            operation === draftSaveOperation
        );
      });
      // A canceled/rejected request must never poison the next queued save.
      draftSaveTail = result.then(
        () => undefined,
        () => undefined
      );
      return result;
    }
    return {
      afterCreate() {
        addDisposer(
          self,
          onPatch(self, (patch) => {
            if (
              patch.path === "/selectedProjectId" ||
              patch.path === "/selectedVideoPartName" ||
              (patch.path === "/projectWorkspaceVersion" &&
                patch.op !== "remove" &&
                patch.value === null)
            ) {
              draftSaveGeneration++;
              if (self.saveModsStatus === AsyncOpState.Saving)
                self.updateSaveModsStatus(AsyncOpState.Changed);
              if (self.saveProjectSchemaStatus === AsyncOpState.Saving)
                self.updateSaveProjectSchemaStatus(AsyncOpState.Changed);
              if (
                self.saveProjectLiveControlSchemaStatus === AsyncOpState.Saving
              )
                self.updateSaveProjectLiveControlSchemaStatus(
                  AsyncOpState.Changed
                );
            }
          })
        );
      },
      getSceneJsonCompressor() {
        if (typeof playerCompress === "function") {
          return playerCompress;
        }

        const windowPlayer = (window as any)?.BlingsPlayer;
        const windowCompressCandidates = [
          windowPlayer?.compress,
          windowPlayer?.helpers?.compress,
          windowPlayer?.helpers?.utils?.compress,
        ];

        for (const windowCompress of windowCompressCandidates) {
          if (typeof windowCompress === "function") {
            return windowCompress;
          }
        }

        throw new Error(
          "Blings player JSON compression helper is not available"
        );
      },
      async uploadSelectedVideoPartJsonDraft(
        jsonDraft = getRoot<RootInstance>(self).playerStore.jsonFile
      ): Promise<string> {
        if (!self.selectedVideoPartName) {
          throw new Error("selected video part is not defined");
        }

        if (!jsonDraft) {
          throw new Error("json draft is not available");
        }

        const compress = this.getSceneJsonCompressor();
        const compressedJson = compress(jsonDraft);
        const fileName = buildSceneJsonAssetName(self.selectedVideoPartName);
        const fullAssetName = this.getFullUrlOfAsset(fileName, "json");
        const file = new File([compressedJson], fileName, {
          type: "application/json",
        });
        const res = await this.uploadFileToS3(fullAssetName, file, file.type);

        return `${CDN[getENV()]}/${res.key}`;
      },
      async logout() {
        await signOutAndDeleteCookies(del);
        // await Auth.signOut();
        window.location.reload();
      },
      setProjects(projects: ProjectBasicInfo[]) {
        self.initialProjectLoadSuccess = true;
        self.hasLoadedProjectList = true;
        self.allProjects.replace(projects);
      },
      mergeProjects(projects: ProjectBasicInfo[]) {
        if (!projects.length) return;

        const projectMap = new Map<string, ProjectBasicInfo>();
        self.allProjects.forEach((project) => {
          if (project.id) projectMap.set(project.id, project);
        });
        projects.forEach((project) => {
          if (project.id) projectMap.set(project.id, project);
        });

        const mergedProjects = Array.from(projectMap.values()).sort((a, b) => {
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
        });

        self.initialProjectLoadSuccess = true;
        self.allProjects.replace(mergedProjects);
      },
      addProject(project: ProjectBasicInfo) {
        const projectIndex = self.allProjects.findIndex(
          (p) => p.id === project.id
        );
        if (projectIndex !== -1) {
          this.replaceProjectByIndex(projectIndex, project);
        } else {
          self.allProjects.push(project);
        }
      },

      renameProjectDataReferences(
        oldKey: string,
        newKey: string,
        scope: DynamicReferenceScope
      ) {
        if (!self.projectWorkspaceVersion || oldKey === newKey) return;
        const rename = { id: ++nextRenameId, oldKey, newKey, scope };
        const next = applyProjectReferenceRenames(
          self.projectWorkspaceVersion,
          [rename]
        );
        const activeMods = getRoot<RootInstance>(self).modsStore.JsonConfig;
        const activeProject = {
          videoParts: [{ modsArr: activeMods }],
        } as ProjectVersion;
        if (
          next === self.projectWorkspaceVersion &&
          applyProjectReferenceRenames(activeProject, [rename]) ===
            activeProject
        )
          return;
        self.pendingProjectReferenceRenames.push(rename);
        self.projectWorkspaceVersion = next;
      },
      clearSavedProjectReferenceRenames(ids: readonly number[]) {
        const saved = new Set(ids);
        self.pendingProjectReferenceRenames.replace(
          self.pendingProjectReferenceRenames.filter(
            (rename) => !saved.has(rename.id)
          )
        );
      },
      setWorkspaceProject(project: ProjectVersion | null) {
        if (!project) self.pendingProjectReferenceRenames.clear();
        const sanitizedProject = project?.flowDiagram
          ? {
              ...project,
              flowDiagram: encodeFlowDiagramSourceHandles(project.flowDiagram),
            }
          : project;

        self.projectWorkspaceVersion = sanitizedProject;
        if (sanitizedProject?.flowDiagram) {
          const sanitizedFlowDiagram = {
            nodes: sanitizedProject.flowDiagram.nodes || [],
            edges: sanitizedProject.flowDiagram.edges || [],
            isDirty: false,
          };
          if (self.projectWorkspaceFlowDiagram) {
            self.projectWorkspaceFlowDiagram.setNodesAndEdges(
              sanitizedFlowDiagram.nodes,
              sanitizedFlowDiagram.edges
            );
            self.projectWorkspaceFlowDiagram.setIsDirty(false);
          } else
            self.setProjectWorkspaceFlowDiagramModel(
              //@ts-ignore
              FlowDiagramModel.create(sanitizedFlowDiagram)
            );
        } else {
          self.setProjectWorkspaceFlowDiagramModel(
            FlowDiagramModel.create({
              edges: [],
              nodes: [],
              isDirty: false,
            })
          );
        }
      },
      setVideoParts(vdps) {
        if (self.projectWorkspaceVersion?.videoParts) {
          const reorderedProject = {
            ...self.projectWorkspaceVersion,
            videoParts: vdps,
          };
          self.projectWorkspaceVersion = reorderedProject;
        }
      },
      async loadProject(projectId: string) {
        if (!projectId) {
          return;
        }
        self.setLoadingProjectList(true);
        try {
          const project = await getPublishedProjectBasicInfo(projectId);
          if (!project) {
            return;
          }
          self.setInitialProjectLoadSuccess(true);
          this.addProject(project);
        } catch (error) {
          console.error("Failed to load project", error);
          throw error;
        } finally {
          self.setLoadingProjectList(false);
        }
      },

      async loadProjects() {
        if (self.isLoadingProjectList) {
          return;
        }

        self.hasLoadedProjectList = false;
        self.setLoadingProjectList(true);
        try {
          const { accountStore } = getRoot<RootInstance>(self);
          const accountIds = accountStore.allAccountIds;

          if (accountIds.length === 0) {
            console.error("No account IDs found");
            return;
          }

          const listProjectBasicInfo = `query ListProjectsByAccount(
          $projectAccountId: ID!
          $sortDirection: ModelSortDirection
          $filter: ModelProjectFilterInput
          $limit: Int
          $nextToken: String
        ) {
          listProjectsByAccount(
            projectAccountId: $projectAccountId
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              title
              updatedAt
              createdAt
              publishedAt
              projectAccountId
              thumbS3Url
              description
            }
            nextToken
          }
        }`;

          const authToken = await getAccessTokenForLambdaAuth();

          const loadProjectsForAccount = async (
            accountId: string
          ): Promise<ProjectBasicInfo[]> => {
            const projects: ProjectBasicInfo[] = [];
            let nextToken: string | null = null;

            do {
              const projectsData = (await client.graphql({
                query: listProjectBasicInfo,
                variables: {
                  projectAccountId: accountId,
                  sortDirection: "DESC",
                  nextToken,
                },
                authMode: "lambda",
                authToken,
              })) as { data: any };

              nextToken =
                projectsData?.data?.listProjectsByAccount?.nextToken || null;
              const items: ProjectBasicInfo[] =
                projectsData?.data?.listProjectsByAccount?.items || [];
              if (items.length) {
                projects.push(...items);
                this.mergeProjects(items);
              }
            } while (nextToken);

            return projects;
          };

          const projectArrays = await Promise.all(
            accountIds.map(async (accountId) => {
              try {
                return await loadProjectsForAccount(accountId);
              } catch (error) {
                console.error(
                  `Error loading projects for account ${accountId}:`,
                  error
                );
                return [];
              }
            })
          );

          const allProjects = projectArrays.flat();

          allProjects.sort((a, b) => {
            return (
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
            );
          });

          const filteredProjects = allProjects.filter((p) => !!p.id);
          const selectedId = self.selectedProjectId;
          if (
            selectedId &&
            !filteredProjects.some((p) => p.id === selectedId)
          ) {
            const currentSelected = self.allProjects.find(
              (p) => p.id === selectedId
            );
            if (currentSelected) {
              console.warn(
                `Selected project ${selectedId} not found in fetched projects, preserving from cache`
              );
              filteredProjects.unshift(currentSelected);
            }
          }

          this.setProjects(filteredProjects);
        } finally {
          self.setLoadingProjectList(false);
        }
      },
      replaceProjectByIndex(index: number, project: ProjectBasicInfo) {
        self.allProjects[index] = project;
      },
      async publishCurrentVersion() {
        if (self.projectWorkspaceVersion) {
          const conflictFields = getPublishConflictFields(
            getRoot<RootInstance>(self)
          );
          const canPublish = await guardPublishedSave(
            self.selectedProjectId || self.projectWorkspaceVersion.id,
            conflictFields,
            self.projectWorkspaceVersion,
            "publish"
          );
          if (!canPublish) {
            return;
          }
          const publishedProjectResponse = await publishVersion(
            self.projectWorkspaceVersion
          );
          const publishedProject = publishedProjectResponse.data?.updateProject;
          updateProjectConflictBaselineFields({
            projectId:
              self.selectedProjectId || self.projectWorkspaceVersion.id,
            target: "published",
            fields: conflictFields,
            values: publishedProject || self.projectWorkspaceVersion,
          });
          // Instead of reloading all projects, fetch and update the specific project
          const updatedProjectInfo = await getPublishedProjectBasicInfo(
            self.selectedProjectId || ""
          );
          if (updatedProjectInfo) {
            const projectIndex = self.allProjects.findIndex(
              (p) => p.id === self.selectedProjectId
            );
            if (projectIndex !== -1) {
              this.replaceProjectByIndex(projectIndex, updatedProjectInfo);
            }
          }
          // await this.loadProjects(); // Removed the slow call
        } else throw new Error("Workspace version is not defined");
      },
      updateSelectedVideoPart(name: string | null) {
        self.selectedVideoPartName = name;
      },
      setSelectedProjectBasicInfo(projectBasicInfo: ProjectBasicInfo) {
        if (!projectBasicInfo.experimentOptimizationTechnique)
          projectBasicInfo.experimentOptimizationTechnique =
            ExperimentOptimizationTechnique.DISABLED;
        self.selectedProjectBasicInfo = projectBasicInfo;
      },

      setExperiments(experiments: Array<Experiment>) {
        if (self.projectWorkspaceVersion)
          self.projectWorkspaceVersion.experiments = experiments;
      },
      async subscribeToProject(pid: string) {
        selectedProjectSubscription?.unsubscribe();

        const accountId = (
          self.selectedProjectBasicInfo as ProjectBasicInfo | null
        )?.projectAccountId;
        if (!accountId) {
          console.error(
            "Cannot subscribe to project: account ID not available"
          );
          return;
        }

        selectedProjectSubscription = subscriptionManager.subscribeToProject(
          accountId,
          pid,
          (basicInfo: ProjectBasicInfo) => {
            // Ignore updates for projects that don't match the currently selected project
            if (basicInfo?.id && basicInfo.id !== self.selectedProjectId) {
              return;
            }
            this.setSelectedProjectBasicInfo(basicInfo);
          },
          (error) => {
            console.error("Project subscription error:", error);
          }
        );
      },

      async subscribeToProjectDraft(pid: string) {
        projectVersionSubscription?.unsubscribe();

        const accountId = (
          self.selectedProjectBasicInfo as ProjectBasicInfo | null
        )?.projectAccountId;
        if (!accountId) {
          console.error(
            "Cannot subscribe to project version: account ID not available"
          );
          return;
        }

        // Need the ProjectVersion id to subscribe; fetch the latest draft first.
        const latestDraft = await getLatestProjectVersion(pid);
        const projectVersionId = latestDraft?.id;
        if (!projectVersionId) {
          console.error("Cannot subscribe: ProjectVersion ID not available");
          return;
        }

        projectVersionSubscription =
          subscriptionManager.subscribeToProjectVersion(
            accountId,
            projectVersionId,
            (partialProjectInDb: ProjectVersion) => {
              // Ignore updates for projects that don't match the currently selected project
              if (
                partialProjectInDb?.id &&
                partialProjectInDb.id !== self.selectedProjectId
              ) {
                return;
              }

              const {
                dynamicDataStore,
                modsStore,
                playerStore,
                experimentStore,
              } = getRoot<RootInstance>(self);
              const baselineFields: ProjectConflictField[] = [];
              const hasUnsavedSceneChanges =
                self.hasUnsavedConnectorKeyRenames ||
                modsStore.hasUnsavedChanges ||
                playerStore.hasUnsavedJsonDraftChanges ||
                experimentStore.hasUnsavedChanges;
              if (partialProjectInDb.videoParts && !hasUnsavedSceneChanges) {
                baselineFields.push({
                  path: "videoParts",
                  domain: "connectors",
                });
              }
              if (partialProjectInDb.experiments && !hasUnsavedSceneChanges) {
                baselineFields.push({
                  path: "experiments",
                  domain: "experiments",
                });
              }
              if (
                partialProjectInDb.flowDiagram &&
                !self.projectWorkspaceFlowDiagram?.isDirty
              ) {
                baselineFields.push({ path: "flowDiagram", domain: "flow" });
              }
              if (
                partialProjectInDb.stateJsonSchemaStr &&
                !dynamicDataStore.hasUnsavedChangesPerVideo
              ) {
                baselineFields.push({
                  path: "stateJsonSchemaStr",
                  domain: "data",
                  label: "per-video data",
                });
              }
              if (
                (partialProjectInDb.settings ||
                  partialProjectInDb.settingsJsonSchemaStr) &&
                !dynamicDataStore.hasUnsavedChangesLiveControl
              ) {
                baselineFields.push(...liveControlFields);
              }
              if (baselineFields.length && self.selectedProjectId) {
                updateProjectConflictBaselineFields({
                  projectId: self.selectedProjectId,
                  target: "draft",
                  fields: baselineFields,
                  values: partialProjectInDb,
                });
              }

              const workspaceVersion = mergeProjectVersionSubscriptionPatch(
                self.projectWorkspaceVersion,
                applyProjectReferenceRenames(
                  partialProjectInDb,
                  self.pendingProjectReferenceRenames
                )
              );
              if (!workspaceVersion) return;

              this.setWorkspaceProject(workspaceVersion);
              const selectedVideoPart =
                partialProjectInDb.videoParts &&
                workspaceVersion.videoParts?.find(
                  (videoPart) => videoPart.name === self.selectedVideoPartName
                );
              if (selectedVideoPart) {
                const { modsStore, playerStore } = getRoot<RootInstance>(self);
                if (!playerStore.hasUnsavedJsonDraftChanges) {
                  this.updateJson(selectedVideoPart.jsonUrl);
                }
                modsStore.mergeWithModsFromSubscription(
                  selectedVideoPart.modsArr
                );
              }
              if (partialProjectInDb.settingsJsonSchemaStr) {
                dynamicDataStore.setInitialLiveControSchemaAndData(
                  partialProjectInDb.settingsJsonSchemaStr || "{}",
                  partialProjectInDb.settings || "{}",
                  false
                );
              }
              if (partialProjectInDb.stateJsonSchemaStr) {
                dynamicDataStore.setInitialPerVideoSchema(
                  partialProjectInDb?.stateJsonSchemaStr || "",
                  false
                );
              }
              if (partialProjectInDb.experiments) {
                getRoot<RootInstance>(
                  self
                ).experimentStore.mergeWithExperimentsFromSubscription(
                  partialProjectInDb.experiments
                );
              }
            },
            (error) => {
              console.error("ProjectVersion subscription error:", error);
            }
          );
      },
      updateJsonAndMods(
        jsonVidUrl: string,
        mods?: Array<Mod> | null,
        options?: { resetSession?: boolean }
      ): void {
        this.updateJson(jsonVidUrl, options);
        getRoot<RootInstance>(self).modsStore.replaceMods(mods);
      },
      updateJson(
        jsonVidUrl: string,
        options?: { resetSession?: boolean }
      ): void {
        const playerStore = getRoot<RootInstance>(self).playerStore;
        if (jsonVidUrl) {
          playerStore.downloadJsonFile(jsonVidUrl, options);
        } else {
          playerStore.removePlayer();
        }
      },
      //checkVideoPartName flag is used to check if the video part name is the same as the selected video part name
      // when the project is changed, we don't need to check the video part name
      changeVideoPart(selectedVideoPartName, checkVideoPartName = true) {
        if (
          self.projectWorkspaceVersion &&
          self.projectWorkspaceVersion.videoParts &&
          (checkVideoPartName
            ? selectedVideoPartName !== self.selectedVideoPartName
            : self.selectedVideoPartName &&
              selectedVideoPartName !== self.selectedVideoPartName)
        ) {
          const rootStore = getRoot<RootInstance>(self);
          rootStore.modsStore.setActiveMod(); // Reset active mod
          rootStore.timelineStore.setScrollOffset(0);
          self.selectedVideoPartName = selectedVideoPartName;
          const selectedVideoPart =
            self.projectWorkspaceVersion.videoParts.find(
              (vp) => vp.name === selectedVideoPartName
            );
          if (selectedVideoPart) {
            this.updateJsonAndMods(
              selectedVideoPart.jsonUrl,
              selectedVideoPart.modsArr || null,
              { resetSession: true }
            );
          }
        }
      },

      async deleteVideoPart(videoPartName) {
        if (self.projectWorkspaceFlowDiagram?.isSceneInFlow(videoPartName)) {
          return await confirmationModal.confirm(
            "Scene in use. To delete this scene, first go to the Lineup Builder and remove it from the Flow",
            "Ok"
          );
        }
        const vps = self.projectWorkspaceVersion?.videoParts?.filter(
          (vp) => vp.name !== videoPartName
        );
        if (vps) {
          const input = { id: self.selectedProjectId, videoParts: vps };
          const canSave = await guardDraftSave(
            self.selectedProjectId || "",
            sceneListConflictFields,
            input
          );
          if (!canSave) {
            return;
          }
          const modifiedSub = insertIntoGraphQlString(
            insertIntoGraphQlString(updateProjectVersion, {
              path: ["videoParts"],
              key: "modsArr",
              value: {
                id: true,
                dataStr: true,
                origin: true,
                name: true,
                description: true,
              },
            }),
            {
              path: ["videoParts"],
              key: "fonts",
              value: {
                family: true,
                style: true,
                url: true,
                weight: true,
              },
            }
          );
          const authToken = await getAccessTokenForLambdaAuth();
          await client.graphql({
            query: modifiedSub,
            variables: {
              input,
            },
            authMode: "lambda",
            authToken,
          });
          updateProjectConflictBaselineFields({
            projectId: self.selectedProjectId || "",
            target: "draft",
            fields: sceneListConflictFields,
            values: input,
          });
          const projectWorkspaceVersion = JSON.parse(
            JSON.stringify(self.projectWorkspaceVersion)
          );
          projectWorkspaceVersion.videoParts = vps;
          this.setWorkspaceProject(projectWorkspaceVersion);
        }
      },

      async renameVideoPart(oldName, newName) {
        const vps = self.projectWorkspaceVersion?.videoParts;
        if (vps) {
          const validation = validateSceneRename(
            newName,
            oldName,
            vps.map((videoPart) => videoPart.name)
          );
          if (validation.error || validation.name === oldName) return;
          newName = validation.name;
          const vp = vps.find((vp) => vp.name === oldName);
          if (vp) {
            vp.name = newName;

            vps.forEach((videoPart) => {
              if (videoPart.modsArr) {
                videoPart.modsArr.forEach((mod) => {
                  const modData = JSON.parse(mod.dataStr || "{}");

                  if (modData.type === "interactiveConcat" && modData.value) {
                    let valueUpdated = false;
                    modData.value = modData.value.map((item) => {
                      if (typeof item === "string" && item === oldName) {
                        valueUpdated = true;
                        return newName;
                      }
                      return item;
                    });

                    if (modData.jumpValue === oldName) {
                      modData.jumpValue = newName;
                      valueUpdated = true;
                    }

                    if (valueUpdated) {
                      mod.dataStr = JSON.stringify(modData);
                    }
                  }
                });
              }
            });

            const modsStore = getRoot<RootInstance>(self).modsStore;
            modsStore.mods.forEach((mod) => {
              if (
                mod.moddata.type === "interactiveConcat" &&
                mod.moddata.value
              ) {
                const updatedValue = mod.moddata.value.map((item) => {
                  if (typeof item === "string" && item === oldName) {
                    return newName;
                  }
                  return item;
                });

                const valueChanged = !mod.moddata.value.every(
                  (item, index) => item === updatedValue[index]
                );
                const jumpValueChanged = mod.moddata.jumpValue === oldName;

                if (valueChanged || jumpValueChanged) {
                  const updates = {};
                  if (valueChanged) {
                    updates["value"] = updatedValue;
                  }
                  if (jumpValueChanged) {
                    updates["jumpValue"] = newName;
                  }

                  modsStore.updateValue(
                    mod.id,
                    Object.keys(updates),
                    Object.values(updates)
                  );
                }
              }
            });

            if (self.projectWorkspaceFlowDiagram) {
              self.projectWorkspaceFlowDiagram.updateSceneNameReferences(
                oldName,
                newName
              );
            }

            // A scene node's id embeds its video part name, so the rename has
            // to carry every reference with it. Same idempotent pass that runs
            // on project selection: the node label was just updated, so this
            // node is the only one left non-conforming, and normalizing it
            // rewrites its id plus the edges, nodeLayout entries and branch
            // timeline (interactiveConcat) mods of every video part that point
            // at it. `flowDiagramDBFormat` already hands over a plain-JSON
            // snapshot with the source handles encoded — the helper only walks
            // plain JSON.
            const migration = normalizeFlowNodeIds({
              flowDiagram:
                self.projectWorkspaceFlowDiagram?.flowDiagramDBFormat,
              videoParts: vps,
              nodeLayout: self.projectWorkspaceVersion?.nodeLayout,
            });
            const flow = migration.output.flowDiagram;
            const renamedVps = migration.output.videoParts;
            const nodeLayout = migration.output.nodeLayout;

            const authToken = await getAccessTokenForLambdaAuth();
            const input = {
              id: self.selectedProjectId,
              videoParts: renamedVps,
              flowDiagram: flow,
              // Only written when the node-id rename actually moved entries.
              ...(nodeLayout !== self.projectWorkspaceVersion?.nodeLayout
                ? { nodeLayout }
                : {}),
            };
            const canSave = await guardDraftSave(
              self.selectedProjectId || "",
              [...sceneNameConflictFields, ...flowConflictFields],
              input
            );
            if (!canSave) {
              return;
            }
            await client.graphql({
              query: customUpdateProjectVersionNodeIds,
              variables: {
                input,
              },
              authMode: "lambda",
              authToken,
            });
            updateProjectConflictBaselineFields({
              projectId: self.selectedProjectId || "",
              target: "draft",
              fields: [...sceneNameConflictFields, ...flowConflictFields],
              values: input,
            });
            const projectWorkspaceVersion = JSON.parse(
              JSON.stringify(self.projectWorkspaceVersion)
            );
            projectWorkspaceVersion.videoParts = renamedVps;
            projectWorkspaceVersion.flowDiagram = flow;
            projectWorkspaceVersion.nodeLayout = nodeLayout;
            this.setWorkspaceProject(projectWorkspaceVersion);

            if (self.projectWorkspaceFlowDiagram) {
              self.projectWorkspaceFlowDiagram.setIsDirty(false);
              if (flow) {
                self.projectWorkspaceFlowDiagram.setNodesAndEdges(
                  flow.nodes || [],
                  flow.edges || []
                );
              }
              // A flow's id IS its first scene node's id, and selectedFlow is
              // an MST reference — renaming that scene changes the id and
              // leaves the reference dangling (throws on read). Drop it before
              // the replace and re-point by lineupId, which rename never moves.
              const prevLineupId = flowsStore.selectedFlow?.lineupId;
              flowsStore.resetSelectedFlow();
              flowsStore.setFlows(self.projectWorkspaceFlowDiagram.asFlows);
              const reselected = flowsStore.flows.find(
                (f) => f.lineupId === prevLineupId
              );
              if (reselected) flowsStore.setSelectedFlow(reselected.id);
            }

            // The selected video part's mods live in ModsStore with edits that
            // are not in videoParts yet, so they are patched rather than
            // reloaded from the normalized copy.
            if (migration.changed) {
              modsStore.mods.forEach((mod) => {
                const moddata = getSnapshot(mod.moddata) as unknown as Record<
                  string,
                  unknown
                >;
                const rewritten = rewriteNodeRefs(
                  moddata,
                  migration.idMap
                ) as Record<string, unknown>;
                if (rewritten === moddata) return;
                const changedKeys = Object.keys(rewritten).filter(
                  (key) => rewritten[key] !== moddata[key]
                );
                modsStore.updateValue(
                  mod.id,
                  changedKeys,
                  changedKeys.map((key) => rewritten[key])
                );
              });
            }

            if (self.selectedVideoPartName === oldName) {
              this.changeVideoPart(newName);
              window.history.replaceState(
                {},
                "",
                scenePath(self.selectedProjectId || "", newName)
              );
              // BrowserRouter only observes popstate, not native pushState.
              window.dispatchEvent(new PopStateEvent("popstate"));
            }
          }
        }
      },

      async createStarterScene() {
        const projectId = self.selectedProjectId;
        const videoParts = self.projectWorkspaceVersion?.videoParts;
        if (!projectId || !videoParts) {
          return undefined;
        }

        const vps = JSON.parse(JSON.stringify(videoParts));
        const sceneName = buildUniqueSceneName(vps.map((vp) => vp.name));
        const flowDiagram = encodeFlowDiagramSourceHandles(
          withSceneFlowNode(
            self.projectWorkspaceFlowDiagram?.flowDiagramDBFormat ||
              self.projectWorkspaceVersion?.flowDiagram,
            sceneName
          )
        );
        const canSave = await guardDraftSave(
          projectId,
          [...sceneListConflictFields, ...flowConflictFields],
          {
            id: projectId,
            flowDiagram,
            videoParts: [
              ...vps,
              {
                name: sceneName,
                jsonUrl: "",
                modsArr: [],
              },
            ],
          }
        );
        if (!canSave) {
          return undefined;
        }

        const starterScene = buildStarterSceneAnimation(sceneName);
        const compress = this.getSceneJsonCompressor();
        const compressedJson = compress(starterScene);
        const fileName = buildSceneJsonAssetName(sceneName);
        const fullAssetName = this.getFullUrlOfAsset(fileName, "json");
        const file = new File([compressedJson], fileName, {
          type: "application/json",
        });
        const res = await this.uploadFileToS3(fullAssetName, file, file.type);
        const newScene = {
          name: sceneName,
          jsonUrl: `${CDN[getENV()]}/${res.key}`,
          modsArr: [],
        };
        const nextVideoParts = [...vps, newScene];
        const authToken = await getAccessTokenForLambdaAuth();
        const input = {
          id: projectId,
          videoParts: nextVideoParts,
          flowDiagram,
        };
        await client.graphql({
          query: customUpdateProjectVersionNodeIds,
          variables: {
            input,
          },
          authMode: "lambda",
          authToken,
        });
        try {
          updateProjectConflictBaselineFields({
            projectId,
            target: "draft",
            fields: [...sceneListConflictFields, ...flowConflictFields],
            values: input,
          });
        } catch (error) {
          console.warn("Failed to update scene conflict baseline", error);
        }
        try {
          this.syncCreatedStarterScene(
            nextVideoParts as NonNullable<ProjectVersion["videoParts"]>,
            sceneName
          );
          this.setWorkspaceProject({
            ...self.projectWorkspaceVersion,
            videoParts: nextVideoParts,
            flowDiagram,
          } as ProjectVersion);
        } catch (error) {
          console.warn("Failed to sync newly created scene locally", error);
        }

        try {
          const { modsStore, playerStore } = getRoot<RootInstance>(self);
          modsStore.setActiveMod();
          modsStore.replaceMods([]);
          playerStore.setJsonFile(starterScene, fileName, {
            markAsLoaded: true,
            jsonUrl: newScene.jsonUrl,
          });
          playerStore.runPlayer({ resetSession: true, immediate: true });
        } catch (error) {
          console.warn("Failed to hydrate newly created scene locally", error);
        }

        return sceneName;
      },

      async duplicateScene(sceneName: string) {
        const vps = JSON.parse(
          JSON.stringify(self.projectWorkspaceVersion?.videoParts)
        );
        if (vps) {
          const vpIndex = vps.findIndex((vp) => vp.name === sceneName);
          if (vpIndex > -1) {
            const vp = vps[vpIndex];
            const duplicatedVp = {
              ...vp,
              modsArr: vp.modsArr.map((mod) => ({
                ...mod,
                id: Math.floor(Math.random() * 100000),
              })),
            };
            let newScene = JSON.parse(JSON.stringify(duplicatedVp));
            const existingNames = new Set(
              self.projectWorkspaceVersion?.videoParts?.map((p) => p.name)
            );
            while (existingNames.has(newScene.name)) {
              newScene.name += " copy";
            }

            vps.splice(vpIndex + 1, 0, newScene);
            const flowDiagram = withSceneFlowNode(
              self.projectWorkspaceFlowDiagram?.flowDiagramDBFormat ||
                self.projectWorkspaceVersion?.flowDiagram,
              newScene.name
            );
            const authToken = await getAccessTokenForLambdaAuth();
            const input = {
              id: self.selectedProjectId,
              videoParts: vps,
              flowDiagram: encodeFlowDiagramSourceHandles(flowDiagram),
            };
            const canSave = await guardDraftSave(
              self.selectedProjectId || "",
              [...sceneListConflictFields, ...flowConflictFields],
              input
            );
            if (!canSave) {
              return;
            }
            await client.graphql({
              query: customUpdateProjectVersionNodeIds,
              variables: {
                input,
              },
              authMode: "lambda",
              authToken,
            });
            updateProjectConflictBaselineFields({
              projectId: self.selectedProjectId || "",
              target: "draft",
              fields: [...sceneListConflictFields, ...flowConflictFields],
              values: input,
            });
            const projectWorkspaceVersion = JSON.parse(
              JSON.stringify(self.projectWorkspaceVersion)
            );
            projectWorkspaceVersion.videoParts = vps;
            projectWorkspaceVersion.flowDiagram = input.flowDiagram;
            this.setWorkspaceProject(projectWorkspaceVersion);
            self.projectWorkspaceFlowDiagram?.setNodesAndEdges(
              input.flowDiagram.nodes,
              input.flowDiagram.edges
            );
            self.projectWorkspaceFlowDiagram?.setIsDirty(false);
          }
        }
      },

      async updateVideoPartDescription(
        videoPartName: string,
        newDescription: string
      ) {
        const vps = self.projectWorkspaceVersion?.videoParts;
        if (vps) {
          const vp = vps.find((vp) => vp.name === videoPartName);
          if (vp) {
            vp.description = newDescription;
            const modifiedSub = insertIntoGraphQlString(
              insertIntoGraphQlString(updateProjectVersion, {
                path: ["videoParts"],
                key: "modsArr",
                value: {
                  id: true,
                  dataStr: true,
                  origin: true,
                  name: true,
                  description: true,
                },
              }),
              {
                path: ["videoParts"],
                key: "fonts",
                value: {
                  family: true,
                  style: true,
                  url: true,
                  weight: true,
                },
              }
            );
            const authToken = await getAccessTokenForLambdaAuth();
            const input = { id: self.selectedProjectId, videoParts: vps };
            const canSave = await guardDraftSave(
              self.selectedProjectId || "",
              sceneDescriptionConflictFields,
              input
            );
            if (!canSave) {
              return;
            }
            await client.graphql({
              query: modifiedSub,
              variables: {
                input,
              },
              authMode: "lambda",
              authToken,
            });
            updateProjectConflictBaselineFields({
              projectId: self.selectedProjectId || "",
              target: "draft",
              fields: sceneDescriptionConflictFields,
              values: input,
            });
            const projectWorkspaceVersion = JSON.parse(
              JSON.stringify(self.projectWorkspaceVersion)
            );
            projectWorkspaceVersion.videoParts = vps;
            this.setWorkspaceProject(projectWorkspaceVersion);
          }
        }
      },

      savePerVideoSchema(schema: any, expectedCurrentSchema?: string) {
        const capturedSchema =
          getRoot<RootInstance>(self).dynamicDataStore.perVideoCurrentSchema;
        const submittedSchema = JSON.parse(JSON.stringify(schema));
        return queueDraftSave((isCurrent) => {
          if (
            getRoot<RootInstance>(self).dynamicDataStore
              .perVideoCurrentSchema !== capturedSchema
          ) {
            throw new Error(staleDraftMessage);
          }
          return this.savePerVideoSchemaNow(
            submittedSchema,
            expectedCurrentSchema,
            isCurrent
          );
        });
      },
      async savePerVideoSchemaNow(
        schema: any,
        expectedCurrentSchema: string | undefined,
        isCurrent: () => boolean
      ) {
        const schemaAtSave =
          getRoot<RootInstance>(self).dynamicDataStore.perVideoCurrentSchema;
        if (
          expectedCurrentSchema !== undefined &&
          getRoot<RootInstance>(self).dynamicDataStore.perVideoCurrentSchema !==
            expectedCurrentSchema
        ) {
          throw new Error(
            "Data changed while saving. Save again to include the latest changes."
          );
        }
        if (self.hasUnsavedConnectorKeyRenames) {
          return this.updateVideoPartModsNow(
            { stateJsonSchemaStr: JSON.stringify(schema) },
            isCurrent
          );
        }
        if (
          getRoot<RootInstance>(self).dynamicDataStore.perVideoInitialSchema ===
          JSON.stringify(schema)
        )
          return;
        if (!self.projectWorkspaceVersion) {
          return;
        }
        /* const schema = createSchemaFromData(data); */
        self.updateSaveProjectSchemaStatus(AsyncOpState.Saving);
        try {
          const modifiedUpdate = `
          mutation UpdateProjectVersion(
            $input: UpdateProjectVersionInput!
          ) {
            updateProjectVersion(input: $input) {
              id
              stateJsonSchemaStr
              accountOwner
              updatedAt
              createdAt
            }
          }
        `;
          const authToken = await getAccessTokenForLambdaAuth();
          if (!isCurrent()) return false;
          const input = {
            id: self.selectedProjectId,
            stateJsonSchemaStr: JSON.stringify(schema),
          };
          const canSave = await guardDraftSave(
            self.selectedProjectId || "",
            perVideoDataFields,
            input
          );
          if (!isCurrent()) return false;
          if (!canSave) {
            self.updateSaveProjectSchemaStatus(AsyncOpState.Changed);
            return false;
          }
          await client.graphql({
            query: modifiedUpdate,
            variables: {
              input,
            },
            authMode: "lambda",
            authToken,
          });
          if (!isCurrent()) return false;
          updateProjectConflictBaselineFields({
            projectId: self.selectedProjectId || "",
            target: "draft",
            fields: perVideoDataFields,
            values: input,
          });
          const projectWorkspaceVersion = JSON.parse(
            JSON.stringify(self.projectWorkspaceVersion)
          );

          projectWorkspaceVersion.stateJsonSchemaStr = JSON.stringify(schema);
          this.setWorkspaceProject(projectWorkspaceVersion);
          const { dynamicDataStore } = getRoot<RootInstance>(self);
          if (dynamicDataStore.perVideoCurrentSchema === schemaAtSave) {
            dynamicDataStore.setInitialPerVideoSchema(
              projectWorkspaceVersion.stateJsonSchemaStr
            );
          } else {
            dynamicDataStore.markSchemasSaved(input);
          }

          self.updateSaveProjectSchemaStatus(AsyncOpState.Success);
        } catch (e) {
          if (!isCurrent()) return false;
          self.updateSaveProjectSchemaStatus(AsyncOpState.Error);
          console.error("err", e);
          setTimeout(() => {
            if (!isCurrent()) return;
            self.updateSaveProjectSchemaStatus(AsyncOpState.Changed);
          }, 2000);
          throw e;
        }

        setTimeout(() => {
          if (!isCurrent()) return;
          self.updateSaveProjectSchemaStatus(AsyncOpState.Changed);
        }, 2000);
      },
      saveLiveControlSchema(
        schema: any,
        data: any,
        expectedCurrentSchema?: string,
        expectedCurrentData?: unknown
      ) {
        const store = getRoot<RootInstance>(self).dynamicDataStore;
        const capturedSchema = store.liveControlCurrentSchema;
        const capturedData = JSON.stringify(store.liveControlCurrentData);
        const submittedSchema = JSON.parse(JSON.stringify(schema));
        const submittedData = JSON.parse(JSON.stringify(data));
        return queueDraftSave((isCurrent) => {
          if (
            store.liveControlCurrentSchema !== capturedSchema ||
            JSON.stringify(store.liveControlCurrentData) !== capturedData
          ) {
            throw new Error(staleDraftMessage);
          }
          return this.saveLiveControlSchemaNow(
            submittedSchema,
            submittedData,
            expectedCurrentSchema,
            expectedCurrentData,
            isCurrent
          );
        });
      },
      async saveLiveControlSchemaNow(
        schema: any,
        data: any,
        expectedCurrentSchema: string | undefined,
        expectedCurrentData: unknown,
        isCurrent: () => boolean
      ) {
        const controlAtSave = getRoot<RootInstance>(self).dynamicDataStore;
        const schemaAtSave = controlAtSave.liveControlCurrentSchema;
        const dataAtSave = JSON.stringify(controlAtSave.liveControlCurrentData);
        if (
          (expectedCurrentSchema !== undefined &&
            schemaAtSave !== expectedCurrentSchema) ||
          (expectedCurrentData !== undefined &&
            dataAtSave !== JSON.stringify(expectedCurrentData))
        ) {
          throw new Error(
            "Data changed while saving. Save again to include the latest changes."
          );
        }
        if (self.hasUnsavedConnectorKeyRenames) {
          return this.updateVideoPartModsNow(
            {
              settingsJsonSchemaStr: JSON.stringify(schema),
              settings: JSON.stringify(data),
            },
            isCurrent
          );
        }
        const dynamicDataStore = getRoot<RootInstance>(self).dynamicDataStore;
        if (
          dynamicDataStore.liveControlInitialSchema ===
            JSON.stringify(schema) &&
          JSON.stringify(dynamicDataStore.initialLiveControlData) ===
            JSON.stringify(data)
        )
          return;
        if (!self.projectWorkspaceVersion) {
          return;
        }
        /* const schema = createSchemaFromData(data); */
        self.updateSaveProjectLiveControlSchemaStatus(AsyncOpState.Saving);
        try {
          const modifiedUpdate = `
          mutation UpdateProjectVersion(
            $input: UpdateProjectVersionInput!
          ) {
            updateProjectVersion(input: $input) {
              id
              accountOwner
              settingsJsonSchemaStr
              settings
              createdAt
              updatedAt
            }
          }
        `;
          const authToken = await getAccessTokenForLambdaAuth();
          if (!isCurrent()) return false;
          const input = {
            id: self.selectedProjectId,
            settingsJsonSchemaStr: JSON.stringify(schema),
            settings: JSON.stringify(data),
          };
          const canSave = await guardDraftSave(
            self.selectedProjectId || "",
            liveControlFields,
            input
          );
          if (!isCurrent()) return false;
          if (!canSave) {
            self.updateSaveProjectLiveControlSchemaStatus(AsyncOpState.Changed);
            return false;
          }
          await client.graphql({
            query: modifiedUpdate,
            variables: {
              input,
            },
            authMode: "lambda",
            authToken,
          });
          if (!isCurrent()) return false;
          updateProjectConflictBaselineFields({
            projectId: self.selectedProjectId || "",
            target: "draft",
            fields: liveControlFields,
            values: input,
          });
          const projectWorkspaceVersion = JSON.parse(
            JSON.stringify(self.projectWorkspaceVersion)
          );
          projectWorkspaceVersion.settings = JSON.stringify(data);
          projectWorkspaceVersion.settingsJsonSchemaStr =
            JSON.stringify(schema);
          this.setWorkspaceProject(projectWorkspaceVersion);
          const { dynamicDataStore } = getRoot<RootInstance>(self);
          if (
            dynamicDataStore.liveControlCurrentSchema === schemaAtSave &&
            JSON.stringify(dynamicDataStore.liveControlCurrentData) ===
              dataAtSave
          ) {
            dynamicDataStore.setInitialLiveControSchemaAndData(
              self.projectWorkspaceVersion.settingsJsonSchemaStr || "{}",
              self.projectWorkspaceVersion.settings || "{}"
            );
          } else {
            dynamicDataStore.markSchemasSaved(input);
          }
          self.updateSaveProjectLiveControlSchemaStatus(AsyncOpState.Success);
        } catch (e) {
          if (!isCurrent()) return false;
          self.updateSaveProjectLiveControlSchemaStatus(AsyncOpState.Error);
          console.error("err", e);
          setTimeout(() => {
            if (!isCurrent()) return;
            self.updateSaveProjectLiveControlSchemaStatus(AsyncOpState.Changed);
          }, 2000);
          throw e;
        }

        setTimeout(() => {
          if (!isCurrent()) return;
          self.updateSaveProjectLiveControlSchemaStatus(AsyncOpState.Changed);
        }, 2000);
      },
      async updateSaveFlowDiagram() {
        try {
          self.updateSaveFlowStatus(AsyncOpState.Saving);
          const modifiedMutation = `
          mutation UpdateProjectVersion(
            $input: UpdateProjectVersionInput!
          ) {
            updateProjectVersion(input: $input) {
              id
              flowDiagram {
                nodes {
                  id
                  label
                  type
                  data
                  lineupId
                }
                edges {
                  source
                  target
                  sourceHandle
                }
              }
              accountOwner
              updatedAt
              createdAt
            }
          }
        `;

          const variables: UpdateProjectVersionMutationVariables = {
            input: {
              id: self.selectedProjectId || "",
              flowDiagram:
                self.projectWorkspaceFlowDiagram?.flowDiagramDBFormat,
            },
          };
          const canSave = await guardDraftSave(
            self.selectedProjectId || "",
            flowConflictFields,
            variables.input
          );
          if (!canSave) {
            self.updateSaveFlowStatus(AsyncOpState.Changed);
            return false;
          }
          const authToken = await getAccessTokenForLambdaAuth();
          await client.graphql({
            query: modifiedMutation,
            variables,
            authMode: "lambda",
            authToken,
          });
          updateProjectConflictBaselineFields({
            projectId: self.selectedProjectId || "",
            target: "draft",
            fields: flowConflictFields,
            values: variables.input,
          });
          const newProjectVersion = JSON.parse(
            JSON.stringify(self.projectWorkspaceVersion)
          );
          newProjectVersion.flowDiagram =
            self.projectWorkspaceFlowDiagram?.flowDiagramDBFormat;
          this.setWorkspaceProject(newProjectVersion);
        } catch (e) {
          self.updateSaveFlowStatus(AsyncOpState.Error);
          console.error("err", e);
          setTimeout(() => {
            self.updateSaveFlowStatus(AsyncOpState.Changed);
          }, 2000);
          throw e;
        }
        self.updateSaveFlowStatus(AsyncOpState.Success);
        setTimeout(() => {
          self.updateSaveFlowStatus(AsyncOpState.Changed);
        }, 2000);
      },

      updateVideoPartMods() {
        return queueDraftSave((isCurrent) =>
          this.updateVideoPartModsNow({}, isCurrent)
        );
      },
      async updateVideoPartModsNow(
        schemaOverrides: {
          stateJsonSchemaStr?: string;
          settingsJsonSchemaStr?: string;
          settings?: string;
        },
        isCurrent: () => boolean
      ) {
        if (
          !self.selectedVideoPartName ||
          !self.projectWorkspaceVersion?.videoParts
        ) {
          return;
        }

        const nextVideoParts = JSON.parse(
          JSON.stringify(self.projectWorkspaceVersion.videoParts)
        );
        const fontOverrides = self.projectWorkspaceVersion.fontOverrides;
        const nextFlowDiagram = self.projectWorkspaceFlowDiagram?.isDirty
          ? self.projectWorkspaceFlowDiagram.flowDiagramDBFormat
          : undefined;
        const vp = nextVideoParts.find(
          (videoPart) => videoPart.name === self.selectedVideoPartName
        );

        if (vp) {
          self.updateSaveModsStatus(AsyncOpState.Saving);
          const root = getRoot<RootInstance>(self);
          const { experimentStore, modsStore, playerStore, dynamicDataStore } =
            root;
          const savedRenameIds = self.pendingProjectReferenceRenames.map(
            (rename) => rename.id
          );
          const savePerVideoRename = self.pendingProjectReferenceRenames.some(
            (rename) => rename.scope === "data"
          );
          const saveControlRename = self.pendingProjectReferenceRenames.some(
            (rename) => rename.scope === "control"
          );
          const renamedSchemaInput = {
            ...(savePerVideoRename
              ? { stateJsonSchemaStr: dynamicDataStore.perVideoCurrentSchema }
              : {}),
            ...(saveControlRename
              ? {
                  settingsJsonSchemaStr:
                    dynamicDataStore.liveControlCurrentSchema,
                  settings: JSON.stringify(
                    dynamicDataStore.liveControlCurrentData
                  ),
                }
              : {}),
            ...schemaOverrides,
          };
          const savedMods = modsStore.JsonConfig as Mod[];
          const savedJsonDraft = playerStore.hasUnsavedJsonDraftChanges
            ? playerStore.jsonFile
            : undefined;
          if (modsStore.hasUnsavedChanges) vp.modsArr = savedMods;
          try {
            if (
              (savePerVideoRename && !renamedSchemaInput.stateJsonSchemaStr) ||
              (saveControlRename && !renamedSchemaInput.settingsJsonSchemaStr)
            ) {
              throw new Error(
                "Cannot save renamed references without their schema"
              );
            }
            if (savedJsonDraft) {
              vp.fonts = getVideoPartFonts(savedJsonDraft, vp.fonts || []);
              vp.jsonUrl = await this.uploadSelectedVideoPartJsonDraft(
                savedJsonDraft
              );
              if (!isCurrent()) return false;
            }

            const modifiedMutation = `
          mutation UpdateProjectVersion(
            $input: UpdateProjectVersionInput!
          ) {
            updateProjectVersion(input: $input) {
              id
              stateJsonSchemaStr
              settingsJsonSchemaStr
              settings
              videoParts {
                name
                jsonUrl
                origin
                playerVersionToUse
                description
                modsArr {
                  id
                  dataStr
                  origin
                  name
                  description

                }
                updatedAt
                hasVideo
                fonts {
                  family
                  style
                  url
                  weight
                }
              }
                experiments {
                  id
                  type
                  variants {
                    id
                    value
                    successFactors {
                      conversionSuccessData
                      engagementSuccessData
                      selectionCount
                      watchTimeSuccessData
                    }
                  }
                }
              flowDiagram {
                nodes {
                  id
                  label
                  type
                  data
                  lineupId
                }
                edges {
                  source
                  target
                  sourceHandle
                }
              }
              accountOwner
              updatedAt
              createdAt
            }
          }
        `;

            const variables: UpdateProjectVersionMutationVariables = {
              input: {
                id: self.selectedProjectId || "",
                ...renamedSchemaInput,
                videoParts: nextVideoParts.map((vp) => {
                  const { __typename, ...rest } = vp;
                  return rest;
                }),
                //@ts-ignore
                experiments: experimentStore.changedExperiments || null,
                flowDiagram: nextFlowDiagram,
                fontOverrides:
                  typeof fontOverrides === "undefined"
                    ? undefined
                    : fontOverrides?.map(toProjectFontInput) || null,
              },
            };
            const conflictFields: ProjectConflictField[] = [
              ...getDirtySceneConflictFields(root),
              ...(renamedSchemaInput.stateJsonSchemaStr !== undefined
                ? perVideoDataFields
                : []),
              ...(renamedSchemaInput.settingsJsonSchemaStr !== undefined
                ? liveControlFields
                : []),
              ...(typeof nextFlowDiagram !== "undefined"
                ? flowConflictFields
                : []),
              ...(typeof fontOverrides !== "undefined"
                ? ([
                    { path: "fontOverrides", domain: "branding" },
                  ] as ProjectConflictField[])
                : []),
            ];
            const canSave = await guardDraftSave(
              self.selectedProjectId || "",
              conflictFields,
              variables.input
            );
            if (!isCurrent()) return false;
            if (!canSave) {
              self.updateSaveModsStatus(AsyncOpState.Changed);
              return false;
            }
            const authToken = await getAccessTokenForLambdaAuth();
            if (!isCurrent()) return false;
            await client.graphql({
              query: modifiedMutation,
              variables,
              authMode: "lambda",
              authToken,
            });
            if (!isCurrent()) return false;
            updateProjectConflictBaselineFields({
              projectId: self.selectedProjectId || "",
              target: "draft",
              fields: conflictFields,
              values: variables.input,
            });
            modsStore.setUnchangedMods(savedMods);
            this.clearSavedProjectReferenceRenames(savedRenameIds);
            if (savedRenameIds.length) {
              dynamicDataStore.markSchemasSaved(renamedSchemaInput);
            }
            experimentStore.changedToUnchanged();
            if (savedJsonDraft) {
              playerStore.setJsonBaseline(savedJsonDraft, vp.jsonUrl);
            }

            if (self.projectWorkspaceFlowDiagram?.isDirty) {
              self.projectWorkspaceFlowDiagram.setIsDirty(false);
            }

            const newProjectVersion = JSON.parse(
              JSON.stringify(self.projectWorkspaceVersion)
            );
            Object.assign(newProjectVersion, renamedSchemaInput);
            newProjectVersion.videoParts = applyProjectReferenceRenames(
              { ...newProjectVersion, videoParts: nextVideoParts },
              self.pendingProjectReferenceRenames
            ).videoParts;
            if (typeof nextFlowDiagram !== "undefined") {
              newProjectVersion.flowDiagram = nextFlowDiagram;
            }
            if (typeof fontOverrides !== "undefined") {
              newProjectVersion.fontOverrides = fontOverrides || null;
            }
            newProjectVersion.experiments =
              experimentStore.unchangedExperiments;
            this.setWorkspaceProject(newProjectVersion);
            self.updateSaveModsStatus(AsyncOpState.Success);
          } catch (e) {
            if (!isCurrent()) return false;
            self.updateSaveModsStatus(AsyncOpState.Error);
            console.error("err", e);
            toast.info("Changes were not saved!", {
              transition: Slide,
            });
            setTimeout(() => {
              if (!isCurrent()) return;
              self.updateSaveModsStatus(AsyncOpState.Changed);
            }, 2000);
            throw e;
          }

          setTimeout(() => {
            if (!isCurrent()) return;
            self.updateSaveModsStatus(AsyncOpState.Changed);
          }, 2000);
          return {
            savedPerVideoSchema:
              renamedSchemaInput.stateJsonSchemaStr !== undefined,
            savedLiveControlSchema:
              renamedSchemaInput.settingsJsonSchemaStr !== undefined,
          };
        }
      },
      getFullUrlOfAsset(assetOriginalName, folder) {
        return (
          self.platformStorageProjectAssetsRoot +
          `/${folder}/${Date.now() + "_" + assetOriginalName}`
        );
      },
      getOriginalNameOfAsset(fullName: string) {
        if (fullName.includes(self.platformStorageProjectAssetsRoot)) {
          const fileName = fullName.split("/").pop(); // remove entire folder structure
          if (fileName) {
            const assetName = fileName.split("_").slice(1).join("_");
            if (assetName) return assetName;
          }
        }
        return fullName;
      },

      async uploadAssetToProject(
        file: File | UploadFile<any>,
        s3Folder: string,
        cb?: (key: string) => void,
        progressCallback?: any
      ) {
        file = file as File;
        self.updateUploadAssetStatus(AsyncOpState.Saving);
        try {
          const _isImage = isImage(file);
          const fullAssetName = this.getFullUrlOfAsset(file.name, s3Folder);
          const res = await this.uploadFileToS3(
            fullAssetName,
            file,
            file.type,
            progressCallback,
            _isImage
          );
          if (isImage(file, ["gif", "webp"])) {
            const res = await this.convertImageToWebP(
              fullAssetName,
              cb,
              progressCallback
            );
            await new Promise((resolve) => setTimeout(resolve, 1000));
            if (cb) {
              if (await canRunWebP()) cb(res.cloudfront.webp);
              else cb(res.cloudfront.original);
            }
          } else {
            if (cb) {
              cb(`${CDN[getENV()]}/${res.key}`);
            }
          }
          self.updateUploadAssetStatus(AsyncOpState.Success);
        } catch (e) {
          await progressCallback({
            loaded: -1,
            total: 100,
            errorMessage:
              "Error when uploading.\nPlease check the uploaded file",
          });

          self.updateUploadAssetStatus(AsyncOpState.Error);
          console.error("err", e);
        }

        setTimeout(() => {
          self.updateUploadAssetStatus(AsyncOpState.Changed);
        }, 2000);
      },

      uploadFileToS3(
        Key: string,
        file: string | File,
        type?: string,
        pcb?: any,
        halveProgress?: boolean
      ) {
        return uploadData({
          key: Key,
          data: file,
          options: {
            accessLevel: "guest", // public
            contentType: type || "application/json",
            onProgress(event) {
              if (pcb) {
                pcb({
                  loaded: event.transferredBytes,
                  total: halveProgress
                    ? (event.totalBytes || 1) * 2
                    : event.totalBytes,
                });
              }
            },
          },
        }).result as Promise<{ key: string }>;
      },

      async convertImageToWebP(
        path: string,
        cb?: (key: string) => void,
        progressCallback?: any
      ): Promise<Image2WebpMicroserviceResponse> {
        const apiURL = "https://cors.blings.io/" + MICROSERVICE_API_URLS.image;
        //const apiURL = "http://localhost:3000/dev/v1/private/convert/image"
        if (progressCallback)
          await progressCallback({ loaded: 60, total: 100 });

        const body = {
          key: path,
        };

        if (progressCallback)
          await progressCallback({ loaded: 75, total: 100 });
        const authSession = await fetchAuthSession();
        const authJWTToken =
          authSession.tokens?.accessToken.toString() as string;
        const res = await fetch(apiURL, {
          method: "POST",
          body: JSON.stringify(body),
          headers: {
            Authorization: authJWTToken,
          },
        });
        const data = (await res.json()) as Image2WebpMicroserviceResponse;

        if (progressCallback)
          await progressCallback({ loaded: 100, total: 100 });

        return data;
      },
      async generateThumbnail() {
        const projectId = self.selectedProjectBasicInfo?.id;
        if (!projectId) return;
        const playerParams = {
          project: {
            id: projectId,
            env: getENV() as SettingsApiEnv,
          },
          data: rootStore.dynamicDataStore.perVideoCurrentData,
          settings: {
            posterFrame: 30,
          },
          scenes: [self.selectedVideoPartName],
        };
        try {
          const response = await post({
            apiName: "BlingsAPI",
            path: `/v2/private/project/${encodeURIComponent(
              projectId
            )}/generateThumbnail`,
            options: { body: playerParams },
          }).response;
          if (response.statusCode >= 400) {
            console.error(
              `[PlatformStore] Thumbnail generation failed: ${response.statusCode}`
            );
          }
        } catch (error) {
          console.error("[PlatformStore] Thumbnail generation error:", error);
        }
      },
    };
  })
  .actions((self) => ({
    selectProject: flow(function* selectProject(
      pid: string,
      sceneName?: string
    ) {
      self.setLoadingSelectedProject(true);
      self.selectedProjectBasicInfo = null;
      self.setWorkspaceProject(null);
      self.selectedProjectId = pid || null;
      if (!pid) {
        self.setLoadingSelectedProject(false);
        return self.updateSelectedVideoPart(null);
      }
      try {
        let jsonVidUrl;
        let mods: Array<Mod> | null | undefined;

        let latestProjectVersion = (yield getLatestProjectVersion(
          pid
        )) as ProjectVersion;
        let publishedProjectForBaseline = yield getPublishedProject(pid);
        if (!latestProjectVersion) {
          // if there is no workspace version, create one out of the published project
          latestProjectVersion = yield createVersion(
            publishedProjectForBaseline
          );
        }
        // Normalize flow node ids to `<videoPartName>_<n>` before the draft
        // becomes the workspace, so the flow diagram, mods and nodeLayout all
        // agree on one set of ids. Idempotent: no-op when ids already conform.
        // The renamed ids are adopted only after the persist succeeds — memory
        // must stay in step with the DB, otherwise a later partial save could
        // strand old node ids inside mods/nodeLayout with no repair possible.
        if (latestProjectVersion) {
          const migration = normalizeFlowNodeIds({
            flowDiagram: latestProjectVersion.flowDiagram,
            videoParts: latestProjectVersion.videoParts,
            nodeLayout: latestProjectVersion.nodeLayout,
          });
          if (migration.changed) {
            console.log(
              `Normalizing ${
                Object.keys(migration.idMap).length
              } scene node id(s) for project ${pid}:`,
              migration.idMap
            );
            const output = migration.output;
            const normalizedInput: UpdateProjectVersionInput = {
              id: latestProjectVersion.id,
              flowDiagram: output.flowDiagram,
            };
            // Only the fields the rename actually touched are written.
            if (output.videoParts !== latestProjectVersion.videoParts) {
              normalizedInput.videoParts = output.videoParts;
            }
            if (output.nodeLayout !== latestProjectVersion.nodeLayout) {
              normalizedInput.nodeLayout = output.nodeLayout;
            }
            try {
              const persisted = (yield persistNormalizedNodeIds(
                normalizedInput
              )) as {
                updatedAt: string | null;
              };
              // Adopt the persisted revision too, so publish-status comparisons
              // don't run against the pre-migration updatedAt.
              latestProjectVersion = {
                ...latestProjectVersion,
                ...output,
                ...(persisted.updatedAt
                  ? { updatedAt: persisted.updatedAt }
                  : {}),
              };
            } catch (e) {
              // Keep the stored ids; the next selection retries the migration.
              console.error("Failed to persist normalized flow node ids", e);
            }
          }
        }
        // Baselines are taken after the normalization persisted, so the draft
        // conflict guard compares against what is actually stored now.
        setProjectConflictBaseline({
          projectId: pid,
          target: "draft",
          snapshot: latestProjectVersion,
        });
        setProjectConflictBaseline({
          projectId: pid,
          target: "published",
          snapshot: publishedProjectForBaseline,
        });
        // TODO subscribe to version and published
        self.setSelectedProjectBasicInfo(
          yield getPublishedProjectBasicInfo(pid)
        );

        if (self.selectedProjectId === pid) {
          self.setWorkspaceProject(latestProjectVersion);

          if (
            self.projectWorkspaceVersion?.videoParts &&
            self.projectWorkspaceVersion.videoParts.length
          ) {
            let selectedVideoPart;
            if (
              sceneName &&
              self.projectWorkspaceVersion.videoParts.some(
                (vp) => vp.name === sceneName
              )
            ) {
              selectedVideoPart = self.projectWorkspaceVersion.videoParts.find(
                (vp) => vp.name === sceneName
              );
            } else {
              selectedVideoPart = self.projectWorkspaceVersion.videoParts[0];
            }
            self.updateSelectedVideoPart(selectedVideoPart.name);

            jsonVidUrl = selectedVideoPart.jsonUrl;
            mods = selectedVideoPart.modsArr;
          }
        }

        if (
          self.projectWorkspaceVersion?.experimentOptimizationTechnique !==
          ExperimentOptimizationTechnique.DISABLED
        ) {
          getRoot<RootInstance>(self).experimentStore.replaceExperiments(
            self.projectWorkspaceVersion?.experiments || []
          );
          getRoot<RootInstance>(
            self
          ).experimentStore.setExperimentOptimizationTechnique(
            (self.selectedProjectBasicInfo as ProjectBasicInfo | null)
              ?.experimentOptimizationTechnique ||
              ExperimentOptimizationTechnique.DISABLED
          );
        }

        self.updateJsonAndMods(jsonVidUrl, mods);
        const { dynamicDataStore } = getRoot<RootInstance>(self);
        dynamicDataStore.setInitialPerVideoSchema(
          latestProjectVersion.stateJsonSchemaStr || "{}"
        );
        dynamicDataStore.setInitialLiveControSchemaAndData(
          latestProjectVersion.settingsJsonSchemaStr || "{}",
          latestProjectVersion.settings || "{}"
        );
        self.subscribeToProject(pid);
        self.subscribeToProjectDraft(pid);

        if (
          !(self.selectedProjectBasicInfo as ProjectBasicInfo | null)
            ?.thumbS3Url
        )
          self.generateThumbnail();
      } finally {
        self.setLoadingSelectedProject(false);
      }
    }),
    resetProjectStore() {
      self.setSelectedSceneName(null);
      self.updateSelectedVideoPart(null);
      self.updateJson("");
      getRoot<RootInstance>(self).modsStore.replaceMods([]);
      getRoot<RootInstance>(self).modsStore.setActiveMod(undefined);
    },
  }));

export type IPlatformModel = Instance<typeof PlatformModel>;
export type IFlowDiagramModel = Instance<typeof FlowDiagramModel>;

export type IFlowDiagramNodeModel = Instance<typeof FlowDiagramNodeModel>;
export type IFlowDiagramEdgeModel = Instance<typeof FlowDiagramEdgeModel>;
